import { useState, useEffect } from 'react';
import { MapPin, Plus, Edit, Trash2, Search, Compass } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useRegionStore } from '@/stores/regionStore';
import { regionsAPI } from '@/api/regions';
import { Button, Loading, ErrorMessage, Input } from '@/components/common';
import { NearestRegionSearch } from '@/components/regions/NearestRegionSearch';
import type { Region, CreateRegionData } from '@/types';

/**
 * RegionsPage Component
 * Admin page for managing regions (Manager role only)
 * Now includes Nearest Region Search for testing
 */
export const RegionsPage = () => {
  const { regions, loading, error, fetchRegions } = useRegionStore();
  const [activeTab, setActiveTab] = useState<'search' | 'manage'>('search'); // Default to search tab
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGovernorate, setSelectedGovernorate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [formData, setFormData] = useState<CreateRegionData>({
    nameEn: '',
    nameAr: '',
    governorateEn: '',
    governorateAr: '',
    isActive: true,
    displayOrder: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // Available governorates
  const governorates = [
    { en: 'Al-Ahmadi', ar: 'الأحمدي' },
    { en: 'Farwaniyah', ar: 'الفروانية' },
    { en: 'Hawalli', ar: 'حولي' },
    { en: 'Jahra', ar: 'الجهراء' },
    { en: 'Kuwait Capital', ar: 'الكويت' },
    { en: 'Mubarak Al-Kabeer', ar: 'مبارك الكبير' },
  ];

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  // Filter regions
  const filteredRegions = regions.filter((region) => {
    const matchesSearch =
      region.nameEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      region.nameAr.includes(searchQuery);
    const matchesGovernorate =
      !selectedGovernorate || region.governorateEn === selectedGovernorate;
    return matchesSearch && matchesGovernorate;
  });

  // Group regions by governorate
  const regionsByGovernorate = filteredRegions.reduce((acc, region) => {
    if (!acc[region.governorateEn]) {
      acc[region.governorateEn] = [];
    }
    acc[region.governorateEn].push(region);
    return acc;
  }, {} as Record<string, Region[]>);

  const handleOpenModal = (region?: Region) => {
    if (region) {
      setEditingRegion(region);
      setFormData({
        nameEn: region.nameEn,
        nameAr: region.nameAr,
        governorateEn: region.governorateEn,
        governorateAr: region.governorateAr,
        isActive: region.isActive,
        displayOrder: region.displayOrder,
        latitude: region.latitude,
        longitude: region.longitude,
        notes: region.notes,
      });
    } else {
      setEditingRegion(null);
      setFormData({
        nameEn: '',
        nameAr: '',
        governorateEn: '',
        governorateAr: '',
        isActive: true,
        displayOrder: 0,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRegion(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingRegion) {
        await regionsAPI.update(editingRegion.id, formData);
        toast.success('تم تحديث المنطقة بنجاح');
      } else {
        await regionsAPI.create(formData);
        toast.success('تم إضافة المنطقة بنجاح');
      }
      handleCloseModal();
      fetchRegions();
    } catch (err: any) {
      toast.error(
        err.response?.data?.error?.message ||
          (editingRegion ? 'فشل تحديث المنطقة' : 'فشل إضافة المنطقة')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (region: Region) => {
    if (
      !window.confirm(
        `هل أنت متأكد من حذف المنطقة "${region.nameEn}"؟ سيتم حذف جميع الأسعار المرتبطة بها.`
      )
    ) {
      return;
    }

    try {
      await regionsAPI.delete(region.id);
      toast.success('تم حذف المنطقة بنجاح');
      fetchRegions();
    } catch (err: any) {
      toast.error(
        err.response?.data?.error?.message || 'فشل حذف المنطقة'
      );
    }
  };

  const handleGovernorateChange = (governorateEn: string) => {
    const gov = governorates.find((g) => g.en === governorateEn);
    if (gov) {
      setFormData({
        ...formData,
        governorateEn: gov.en,
        governorateAr: gov.ar,
      });
    }
  };

  if (loading && regions.length === 0) {
    return <Loading text="جاري تحميل المناطق..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-primary">المناطق</h1>
        </div>
        {activeTab === 'manage' && (
          <Button onClick={() => handleOpenModal()}>
            <Plus className="h-4 w-4 ml-2" />
            إضافة منطقة جديدة
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex gap-2 border-b border-gray-700 pb-2">
          <button
            onClick={() => setActiveTab('search')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'search'
                ? 'bg-primary text-white'
                : 'text-gray-400 hover:text-gray-200 hover:bg-dark'
            }`}
          >
            <Compass className="h-4 w-4" />
            البحث عن أقرب منطقة
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === 'manage'
                ? 'bg-primary text-white'
                : 'text-gray-400 hover:text-gray-200 hover:bg-dark'
            }`}
          >
            <MapPin className="h-4 w-4" />
            إدارة المناطق
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'search' ? (
        // Nearest Region Search Tab
        <NearestRegionSearch />
      ) : (
        // Region Management Tab
        <>
          {error && <ErrorMessage message={error} />}

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="relative">
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="ابحث عن منطقة..."
              icon={<Search className="h-5 w-5" />}
            />
          </div>
          <select
            value={selectedGovernorate}
            onChange={(e) => setSelectedGovernorate(e.target.value)}
            className="w-full bg-dark-lighter border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
          >
            <option value="">جميع المحافظات</option>
            {governorates.map((gov) => (
              <option key={gov.en} value={gov.en}>
                {gov.en} - {gov.ar}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-4 text-sm text-gray-400">
          عرض {filteredRegions.length} من {regions.length} منطقة
        </div>
      </div>

          {/* Regions by Governorate */}
          {Object.entries(regionsByGovernorate).map(([gov, govRegions]) => (
        <div key={gov} className="card">
          <h2 className="text-lg font-bold text-primary mb-4 pb-2 border-b border-gray-700">
            {gov} ({govRegions.length} منطقة)
          </h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>الاسم بالإنجليزية</th>
                  <th>الاسم بالعربية</th>
                  <th>الترتيب</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {govRegions.map((region) => (
                  <tr key={region.id}>
                    <td className="font-medium">{region.nameEn}</td>
                    <td className="font-medium">{region.nameAr}</td>
                    <td className="text-gray-400">{region.displayOrder}</td>
                    <td>
                      <span
                        className={`badge ${
                          region.isActive ? 'badge-success' : 'badge-error'
                        }`}
                      >
                        {region.isActive ? 'نشط' : 'غير نشط'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenModal(region)}
                          className="p-2 hover:bg-dark rounded transition-colors"
                          title="تعديل"
                        >
                          <Edit className="h-4 w-4 text-primary" />
                        </button>
                        <button
                          onClick={() => handleDelete(region)}
                          className="p-2 hover:bg-dark rounded transition-colors"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          ))}

          {/* Modal */}
          {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-dark-lighter border border-gray-700 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-primary mb-4">
              {editingRegion ? 'تعديل منطقة' : 'إضافة منطقة جديدة'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الاسم بالإنجليزية *
                </label>
                <Input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  الاسم بالعربية *
                </label>
                <Input
                  type="text"
                  value={formData.nameAr}
                  onChange={(e) =>
                    setFormData({ ...formData, nameAr: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  المحافظة *
                </label>
                <select
                  value={formData.governorateEn}
                  onChange={(e) => handleGovernorateChange(e.target.value)}
                  required
                  className="w-full bg-dark border border-gray-700 rounded-lg px-4 py-2.5 text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                >
                  <option value="">اختر المحافظة</option>
                  {governorates.map((gov) => (
                    <option key={gov.en} value={gov.en}>
                      {gov.en} - {gov.ar}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  ترتيب العرض
                </label>
                <Input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      displayOrder: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded border-gray-700"
                />
                <label htmlFor="isActive" className="text-sm text-gray-300">
                  منطقة نشطة
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting
                    ? 'جاري الحفظ...'
                    : editingRegion
                    ? 'تحديث'
                    : 'إضافة'}
                </Button>
                <Button
                  type="button"
                  onClick={handleCloseModal}
                  variant="secondary"
                  disabled={submitting}
                  className="flex-1"
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </div>
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegionsPage;
